import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Drawer, Space, Typography, Select, InputNumber, Button, Card, Alert, Spin, message, Input, Tag } from 'antd';
import { CopyOutlined, ThunderboltOutlined, LinkOutlined, ReloadOutlined, RocketOutlined, SafetyOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTonConnectUI } from '@tonconnect/ui-react';
import { Cell } from '@ton/ton';
import { RootState } from '../services/store';
import { useMultiWallet } from '../hooks/useMultiWallet';
import { sendTransactionOne } from '../helpers/transactionService';
import { getPaymentWallet, reportChainDeposit, fetchChainDeposits, ChainDeposit, getVoucherStatus, VoucherStatus, buildOvernightDepositComment } from '../helpers/api';
import { submitLinkedAddress } from '../helpers/walletLink';
import NetworkSelector from './NetworkSelector';
import { isKredafiShell } from '../utils/brand';
import {
  NETWORKS,
  CURRENCIES,
  DEPOSIT_ASSET_ORDER,
  SupportedNetwork,
  SupportedCurrency,
  currencyIcon,
  currencyLabel,
  formatAmount,
  isEvmNetwork,
} from '../constants/networks';

const { Text } = Typography;

interface MultiNetworkDepositDrawerProps {
  open: boolean;
  onClose: () => void;
  activeNetwork: SupportedNetwork;
  /** Вызывается, когда депозит зачислен (или отправлен TON-платёж) — обновить рабочий баланс. */
  onDepositSuccess?: () => void;
  /** Акционная форма ваучера удвоения: метка Voucher:true / apply_voucher. */
  voucherMode?: boolean;
  /** В карточке кабинета KredaFi вместо нижнего Drawer. */
  inline?: boolean;
}

/** Коды причин, для которых у нас есть человеческий текст (см. describeReason). */
const KNOWN_REASONS = [
  'sender_not_linked',
  'linked_after_tx',
  'no_transfer_to_platform',
  'tx_failed_on_chain',
  'awaiting_confirmation',
  'tx_not_found_yet',
];

const STATUS_COLOR: Record<ChainDeposit['status'], string> = {
  pending: 'orange',
  credited: 'green',
  rejected: 'red',
  failed: 'red',
};

/**
 * Пополнение рабочего баланса в выбранной сети (вне Telegram).
 *
 * TON — как раньше: перевод с комментарием через TonConnect, сервер узнаёт
 * депозит по UserId в комментарии.
 * Polygon / Solana / TRON — оплата с подключённого кошелька в один клик, после
 * чего хеш транзакции уходит в report-deposit.php; сервер проверяет перевод в
 * сети и зачисляет. Платить надо именно с подключённого кошелька: перевод с
 * биржи или чужого адреса приписать к аккаунту нельзя.
 */
export const MultiNetworkDepositDrawer: React.FC<MultiNetworkDepositDrawerProps> = ({
  open,
  onClose,
  activeNetwork,
  onDepositSuccess,
  voucherMode = false,
  inline = false,
}) => {
  const { t } = useTranslation();
  const meta = NETWORKS[activeNetwork];
  const wallet = useMultiWallet();
  const { userData } = useSelector((state: RootState) => state.user);
  const usdtJettonWallet = useSelector((state: RootState) => state.usdt.wallet_address);
  const [tonConnectUI] = useTonConnectUI();

  const [selectedCurrency, setSelectedCurrency] = useState<SupportedCurrency>(meta.tokens[0]);
  const [amount, setAmount] = useState<number | null>(100);
  const [platformAddress, setPlatformAddress] = useState<string>('');
  // Сеть закрыта для пополнения, пока в админке не задан мастер-кошелёк.
  const [depositUnavailable, setDepositUnavailable] = useState(false);
  const [memoTag, setMemoTag] = useState<string | null>(null);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [manualHash, setManualHash] = useState('');
  const [deposits, setDeposits] = useState<ChainDeposit[]>([]);
  const [watchingId, setWatchingId] = useState<number | null>(null);
  const [voucher, setVoucher] = useState<VoucherStatus | null>(null);
  const [assetQuery, setAssetQuery] = useState('');
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const kredafi = isKredafiShell();
  const currencyMeta = CURRENCIES[selectedCurrency];
  const minDeposit = currencyMeta.minDeposit;
  const isTon = activeNetwork === 'ton';
  const isConnected = wallet.connectedNetworks[activeNetwork];
  const canOneClick = isTon ? isConnected : wallet.canPayFromWallet(activeNetwork);
  // Кошелёк привязан к аккаунту, но в этой вкладке не подключён (типичный
  // случай: подключали в браузере самого кошелька, вернулись в PWA). Оплата в
  // один клик недоступна, а пополнение — вполне: перевести вручную и прислать
  // хеш. Сервер сверит отправителя со списком привязанных адресов.
  const linkedAddress = wallet.linkedAddresses[activeNetwork];
  const linkedOnly = !isConnected && Boolean(linkedAddress);

  useEffect(() => {
    if (!open || !voucherMode) {
      setVoucher(null);
      return;
    }
    let cancelled = false;
    getVoucherStatus().then((v) => {
      if (!cancelled) setVoucher(v);
    });
    return () => {
      cancelled = true;
    };
  }, [open, voucherMode]);

  const voucherDeposit = amount || minDeposit;
  const voucherMaxBonus =
    (voucher?.max_bonus && (voucher.max_bonus[selectedCurrency]
      ?? (selectedCurrency === 'USDC' ? voucher.max_bonus.USDT : undefined)))
    ?? 0;
  const voucherBonus =
    voucherMaxBonus > 0 ? Math.min(voucherDeposit, voucherMaxBonus) : voucherDeposit;

  useEffect(() => {
    if (kredafi && CURRENCIES[selectedCurrency]?.networks.includes(activeNetwork)) {
      return;
    }
    const next = meta.tokens[0];
    setSelectedCurrency(next);
    setAmount(CURRENCIES[next].stable ? 100 : next === 'SOL' ? 0.5 : 20);
    // Сеть сменилась снаружи (шапка) — подставить первую валюту этой сети.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeNetwork, kredafi]);

  const pickDepositAsset = (code: SupportedCurrency, network?: SupportedNetwork) => {
    const nets = CURRENCIES[code].networks;
    const net =
      network && nets.includes(network)
        ? network
        : nets.includes(activeNetwork)
        ? activeNetwork
        : nets[0];
    setSelectedCurrency(code);
    setAmount(CURRENCIES[code].stable ? 100 : code === 'SOL' ? 0.5 : 20);
    if (net !== activeNetwork) {
      wallet.setActiveNetwork(net);
    }
  };

  const depositAssetQuery = assetQuery.trim().toLowerCase();
  const depositAssets = DEPOSIT_ASSET_ORDER.filter((code) => {
    if (!depositAssetQuery) return true;
    const metaC = CURRENCIES[code];
    const hay = [
      code,
      metaC.label,
      ...metaC.networks.map((n) => NETWORKS[n].name),
    ]
      .join(' ')
      .toLowerCase();
    return hay.includes(depositAssetQuery);
  });

  // Адрес платформы для выбранной сети
  useEffect(() => {
    if (!open) return;
    setLoadingAddress(true);
    setDepositUnavailable(false);
    setPlatformAddress('');
    getPaymentWallet(wallet.addresses[activeNetwork] || wallet.linkedAddresses[activeNetwork] || '', activeNetwork)
      .then((res: any) => {
        if (res?.walletAddress) {
          setPlatformAddress(res.walletAddress);
          setMemoTag(res.memoTag || null);
        } else {
          setDepositUnavailable(true);
        }
      })
      .catch((err) => console.warn('[DepositDrawer] Failed to get platform address:', err))
      .finally(() => setLoadingAddress(false));
  }, [open, activeNetwork, wallet.addresses, wallet.linkedAddresses]);

  const loadDeposits = useCallback(
    async (recheckId?: number) => {
      if (isTon) return;
      try {
        const list = await fetchChainDeposits(recheckId);
        setDeposits(list.filter((d) => d.network === activeNetwork).slice(0, 5));
        return list;
      } catch (e) {
        return [];
      }
    },
    [isTon, activeNetwork]
  );

  useEffect(() => {
    if (open) loadDeposits();
  }, [open, loadDeposits]);

  // Поллинг статуса заявленного депозита: сеть подтверждает за 10–60 секунд.
  useEffect(() => {
    if (!watchingId || !open) return;
    let attempts = 0;
    const tick = async () => {
      attempts++;
      const list = (await loadDeposits(watchingId)) || [];
      const dep = list.find((d) => d.id === watchingId);
      if (dep && dep.status !== 'pending') {
        setWatchingId(null);
        if (dep.status === 'credited') {
          const bonus = dep.voucher_applied ? dep.voucher_bonus : 0;
          message.success(
            bonus
              ? t('Deposit credited: {{amount}} {{currency}}', { amount: formatAmount(dep.currency || '', dep.amount), currency: currencyLabel(dep.currency || '') })
                + ` +${formatAmount(dep.currency || '', bonus)} ${currencyLabel(dep.currency || '')}`
              : t('Deposit credited: {{amount}} {{currency}}', { amount: formatAmount(dep.currency || '', dep.amount), currency: currencyLabel(dep.currency || '') })
          );
          onDepositSuccess?.();
        } else {
          message.error(describeReason(dep.reason));
        }
        return;
      }
      if (attempts < 24) {
        pollTimer.current = setTimeout(tick, 10000);
      } else {
        setWatchingId(null);
        message.info(t('The network has not confirmed the transfer yet. It will be credited automatically once confirmed.'));
      }
    };
    pollTimer.current = setTimeout(tick, 8000);
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchingId, open]);

  const describeReason = (reason: string | null): string => {
    switch (reason) {
      case 'sender_not_linked':
        return t('The transfer was sent from a wallet that is not connected to your account. Pay from the connected wallet.');
      case 'linked_after_tx':
        return t('This wallet was connected to your account after the transfer was made, so the transfer cannot be attributed. Contact support.');
      case 'no_transfer_to_platform':
        return t('This transaction does not contain a transfer to the platform address.');
      case 'tx_failed_on_chain':
        return t('The transaction failed on-chain.');
      case 'awaiting_confirmation':
      case 'tx_not_found_yet':
        return t('Waiting for network confirmation…');
      default:
        return reason ? t('Deposit was not credited') + ` (${reason})` : t('Deposit was not credited');
    }
  };

  const handleCopy = (val: string) => {
    navigator.clipboard.writeText(val);
    message.success(t('Address copied'));
  };

  const submitHash = async (txHash: string, from?: string, amt?: number) => {
    // Сервер зачисляет депозит только с адреса, уже привязанного к аккаунту:
    // иначе чужой перевод на адрес платформы мог бы присвоить кто угодно по
    // хешу из обозревателя блоков. Привязка ставится при подключении кошелька,
    // но между подключением и оплатой она может не успеть доехать — поэтому
    // перед заявкой убеждаемся, что адрес привязан. Повторная привязка того же
    // адреса ничего не меняет.
    if (from && wallet.linkedAddresses[activeNetwork] !== from) {
      try {
        await submitLinkedAddress({
          chain: activeNetwork,
          address: from,
          walletName: NETWORKS[activeNetwork].name + ' Wallet',
        });
        await wallet.refreshLinked();
      } catch (e) {
        // Привязка могла не пройти (адрес занят другим аккаунтом) — пусть
        // об этом скажет ответ на саму заявку, он понятнее.
      }
    }
    const dep = await reportChainDeposit({
      network: activeNetwork,
      tx_hash: txHash,
      currency: selectedCurrency,
      amount: amt,
      from_address: from,
      voucher: voucherMode,
    });
    setDeposits((prev) => [dep, ...prev.filter((d) => d.id !== dep.id)].slice(0, 5));
    if (dep.status === 'credited') {
      const bonus = dep.voucher_applied ? dep.voucher_bonus : 0;
      message.success(
        bonus
          ? t('Deposit credited: {{amount}} {{currency}}', { amount: formatAmount(dep.currency || '', dep.amount), currency: currencyLabel(dep.currency || '') })
            + ` +${formatAmount(dep.currency || '', bonus)} ${currencyLabel(dep.currency || '')}`
          : t('Deposit credited: {{amount}} {{currency}}', { amount: formatAmount(dep.currency || '', dep.amount), currency: currencyLabel(dep.currency || '') })
      );
      onDepositSuccess?.();
    } else if (dep.status === 'pending') {
      message.info(t('Transaction sent. Waiting for network confirmation…'));
      setWatchingId(dep.id);
    } else {
      message.error(describeReason(dep.reason));
    }
  };

  const handleDirectPay = async () => {
    if (!amount || amount < minDeposit) {
      message.error(`${t('Minimum')} ${minDeposit} ${currencyLabel(selectedCurrency)}`);
      return;
    }
    if (!platformAddress) {
      message.error(t("Couldn't get a payment address"));
      return;
    }
    setIsSending(true);
    try {
      if (isTon) {
        const baseComment = voucherMode
          ? `OvernightAddFunds:true;Voucher:true;UserId:${userData?.id || ''}`
          : `OvernightAddFunds:true;UserId:${userData?.id || ''}`;
        const cur = selectedCurrency === 'TON' ? 'TON' : 'USDT';
        const comment = await buildOvernightDepositComment(baseComment, {
          currency: cur,
          amount,
          voucher: voucherMode,
        });
        if (cur === 'USDT' && !usdtJettonWallet) {
          message.error(t('USDT wallet not ready, try again in a moment'));
          return;
        }
        const tx = await sendTransactionOne(comment, cur as any, usdtJettonWallet, platformAddress, amount);
        if (tx && tx.messages.length) {
          onClose();
          const result = await tonConnectUI.sendTransaction(tx);
          const hash = Cell.fromBase64(result.boc).hash().toString('hex');
          console.log('[deposit] tx hash', hash);
          message.success(t('The transaction has been sent'));
          onDepositSuccess?.();
        }
        return;
      }

      const { txHash, from } = await wallet.payFromWallet({
        network: activeNetwork,
        to: platformAddress,
        amount,
        currency: selectedCurrency,
      });
      await submitHash(txHash, from, amount);
    } catch (err: any) {
      console.error('[DepositDrawer] pay error:', err);
      const msg = err?.message || '';
      if (/cancel|reject|denied/i.test(msg)) return;
      if (msg === 'connector_unavailable') {
        message.error(t('Wallet connector is unavailable. Reload the page or try another browser.'));
      } else if (KNOWN_REASONS.includes(msg)) {
        message.error(describeReason(msg));
      } else {
        message.error(msg || t('Payment failed'));
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleManualReport = async () => {
    const hash = manualHash.trim();
    if (!hash) return;
    setIsSending(true);
    try {
      await submitHash(hash, wallet.addresses[activeNetwork] || linkedAddress || undefined, amount || undefined);
      setManualHash('');
    } catch (err: any) {
      const msg = err?.message || '';
      message.error(KNOWN_REASONS.includes(msg) ? describeReason(msg) : msg || t('Failed to report deposit'));
    } finally {
      setIsSending(false);
    }
  };

  const presets = currencyMeta.stable ? [25, 50, 100, 300] : selectedCurrency === 'SOL' ? [0.1, 0.5, 1, 5] : [10, 25, 50, 100];

  const form = (
      <div data-testid={voucherMode ? 'voucher-deposit-form' : 'deposit-form'}>
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        {voucherMode && (
          <Alert
            type="success"
            showIcon
            icon={<ThunderboltOutlined style={{ color: '#52c41a' }} />}
            message={t('Personal Multi-Voucher Doubling (2x)')}
            description={t('When you top up, the amount is automatically doubled and credited to your working balance with a 10% monthly payout.')}
            style={{ borderRadius: 12 }}
          />
        )}
        {voucherMode && !kredafi && (
          <div>
            <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
              {t('Blockchain platforms')}
            </Text>
            <NetworkSelector
              variant={inline ? 'platforms' : 'default'}
              activeNetwork={activeNetwork}
              onSelectNetwork={wallet.setActiveNetwork}
              connectedNetworks={wallet.connectedNetworks}
            />
          </div>
        )}
        <div>
          <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
            {t('Currency')}
          </Text>
          {kredafi ? (
            <div className="kf-deposit-assets" data-testid="deposit-asset-picker">
              <Input
                allowClear
                size="large"
                prefix={<SearchOutlined />}
                placeholder={t('Search currency') as string}
                value={assetQuery}
                onChange={(e) => setAssetQuery(e.target.value)}
                data-testid="deposit-asset-search"
                style={{ marginBottom: 10 }}
              />
              <div className="kf-deposit-asset-grid">
                {depositAssets.map((code) => (
                  <button
                    key={code}
                    type="button"
                    className={`kf-deposit-asset${selectedCurrency === code ? ' on' : ''}`}
                    onClick={() => pickDepositAsset(code)}
                  >
                    <img src={currencyIcon(code)} alt="" width={22} height={22} />
                    <span>{currencyLabel(code)}</span>
                  </button>
                ))}
              </div>
              {depositAssets.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('No currencies found')}
                </Text>
              ) : (
                <div className="kf-deposit-nets">
                  {CURRENCIES[selectedCurrency].networks.map((net) => (
                    <button
                      key={net}
                      type="button"
                      className={`kf-chip${activeNetwork === net ? ' on' : ''}`}
                      onClick={() => pickDepositAsset(selectedCurrency, net)}
                    >
                      {NETWORKS[net].name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <Select
              value={selectedCurrency}
              onChange={(v: SupportedCurrency) => {
                setSelectedCurrency(v);
                setAmount(CURRENCIES[v].stable ? 100 : v === 'SOL' ? 0.5 : 20);
              }}
              style={{ width: '100%' }}
              size="large"
              data-testid="deposit-currency"
            >
              {meta.tokens.map((c) => (
                <Select.Option key={c} value={c}>
                  <Space size={6}>
                    <img src={currencyIcon(c)} alt={c} width={16} height={16} style={{ objectFit: 'contain' }} />
                    <Text strong>{currencyLabel(c)}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      ({meta.name})
                    </Text>
                  </Space>
                </Select.Option>
              ))}
            </Select>
          )}
        </div>

        <div>
          <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
            {t('Amount')} ({currencyLabel(selectedCurrency)})
          </Text>
          <InputNumber
            value={amount}
            onChange={(v) => setAmount(v)}
            min={minDeposit}
            size="large"
            style={{ width: '100%' }}
            placeholder={`${t('Minimum')} ${minDeposit} ${currencyLabel(selectedCurrency)}`}
          />
          <div className={inline ? 'kf-chips' : undefined} style={inline ? undefined : { display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
            {presets.map((preset) => (
              inline ? (
                <button
                  key={preset}
                  type="button"
                  className={`kf-chip${amount === preset ? ' on' : ''}`}
                  onClick={() => setAmount(preset)}
                >
                  {preset} {currencyLabel(selectedCurrency)}
                </button>
              ) : (
                <Button key={preset} size="small" onClick={() => setAmount(preset)} style={{ borderRadius: '8px' }}>
                  {preset} {currencyLabel(selectedCurrency)}
                </Button>
              )
            ))}
          </div>
        </div>

        {voucherMode && (
          <Card size="small" className={inline ? 'kf-voucher-sum' : undefined} style={{ background: '#f6ffed', borderRadius: 12, border: '1px solid #b7eb8f' }}>
            <Space direction="vertical" style={{ width: '100%' }} size={6}>
              <Text strong style={{ color: '#389e0d', fontSize: 13 }}>
                {t('Voucher summary')}:
              </Text>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <Text>{t('Deposit:')}</Text>
                <Text strong>
                  {formatAmount(selectedCurrency, voucherDeposit)} {currencyLabel(selectedCurrency)}
                </Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <Text style={{ color: '#52c41a' }}>+ {t('Voucher bonus (2x):')}</Text>
                <Text strong style={{ color: '#52c41a' }}>
                  +{formatAmount(selectedCurrency, voucherBonus)} {currencyLabel(selectedCurrency)}
                </Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, paddingTop: 4, borderTop: '1px dashed #d9f7be' }}>
                <Text strong>{t('Will be credited:')}</Text>
                <Text strong style={{ color: '#1890ff', fontSize: 15 }}>
                  {formatAmount(selectedCurrency, voucherDeposit + voucherBonus)} {currencyLabel(selectedCurrency)}
                </Text>
              </div>
              {voucherBonus < voucherDeposit && (
                <Text type="warning" style={{ fontSize: 11 }}>
                  {t('Bonus is capped at {{max}} {{currency}} per use.', { max: voucherMaxBonus, currency: currencyLabel(selectedCurrency) })}
                </Text>
              )}
              <Text type="secondary" style={{ fontSize: 11 }}>
                {t('The bonus is credited automatically after the deposit is confirmed on-chain.')}
              </Text>
            </Space>
          </Card>
        )}

        {depositUnavailable && !loadingAddress && (
          <Alert
            type="warning"
            showIcon
            data-testid="deposit-unavailable"
            style={{ borderRadius: 12 }}
            message={t('Deposits in {{network}} are not open yet: the platform deposit address is being configured. Please try again later.', { network: meta.name })}
          />
        )}

        {depositUnavailable ? null : canOneClick ? (
          <Button
            type="primary"
            block
            size="large"
            loading={isSending}
            onClick={handleDirectPay}
            icon={<ThunderboltOutlined />}
            data-testid="deposit-pay"
            className={inline ? 'kf-primary' : undefined}
            style={
              inline
                ? { height: 50, borderRadius: 14, fontWeight: 650, fontSize: 15.5, background: '#1a73e8', borderColor: '#1a73e8' }
                : {
                    height: '48px',
                    borderRadius: '12px',
                    fontWeight: 600,
                    fontSize: '15px',
                    background: meta.color,
                    borderColor: meta.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }
            }
          >
            {voucherMode
              ? t('Top up {{amount}} {{currency}} and Double', {
                  amount: amount || minDeposit,
                  currency: currencyLabel(selectedCurrency),
                })
              : `${t('Top up')} ${amount || minDeposit} ${currencyLabel(selectedCurrency)}`}
          </Button>
        ) : (
          <Button
            block
            size="large"
            icon={<LinkOutlined />}
            onClick={() => wallet.connect(activeNetwork).catch(() => undefined)}
            style={{ height: '46px', borderRadius: '12px', fontWeight: 600 }}
          >
            {t('Connect {{network}} wallet', { network: meta.name })}
          </Button>
        )}

        {!isTon && linkedOnly && (
          <Alert
            type="info"
            showIcon
            data-testid="deposit-linked-only"
            style={{ borderRadius: 12, fontSize: 12 }}
            message={t('Send the transfer from your linked wallet and paste the transaction hash below — it will be credited without connecting the wallet here.')}
            description={
              <Text style={{ fontSize: 11, fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>
                {linkedAddress}
              </Text>
            }
          />
        )}

        {!isTon && !linkedOnly && (
          <Alert
            type="info"
            showIcon
            style={{ borderRadius: 12, fontSize: 12 }}
            message={t('Pay only from the wallet connected to your account. Transfers from exchanges or other wallets cannot be attributed and will not be credited.')}
          />
        )}

        <Card
          size="small"
          className={inline ? 'kf-how' : undefined}
          style={{ background: inline ? '#f7f8fb' : '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0' }}
          bodyStyle={{ padding: '14px 16px' }}
        >
          <Text strong style={{ fontSize: 13, color: '#0f172a', display: 'block', marginBottom: 10 }}>
            {t('How top-up works')}
          </Text>

          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <ThunderboltOutlined style={{ fontSize: 16, lineHeight: 1.2, color: '#64748b', marginTop: 2 }} />
              <div>
                <Text strong style={{ fontSize: 12, color: '#1e293b', display: 'block' }}>
                  {t('Instant crediting')}
                </Text>
                <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.4 }}>
                  {t('Funds are credited to your working balance immediately after network confirmation.')}
                </Text>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <SafetyOutlined style={{ fontSize: 16, lineHeight: 1.2, color: '#64748b', marginTop: 2 }} />
              <div>
                <Text strong style={{ fontSize: 12, color: '#1e293b', display: 'block' }}>
                  {t('Direct on-chain payment')}
                </Text>
                <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.4 }}>
                  {t('Payment is made directly from your connected wallet without intermediaries.')}
                </Text>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <RocketOutlined style={{ fontSize: 16, lineHeight: 1.2, color: '#64748b', marginTop: 2 }} />
              <div>
                <Text strong style={{ fontSize: 12, color: '#1e293b', display: 'block' }}>
                  {t('Ready to earn')}
                </Text>
                <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.4 }}>
                  {t(
                    isKredafiShell()
                      ? "Place overnight or on-demand from working balance."
                      : "Use your working balance to issue overnight or on-demand loans."
                  )}
                </Text>
              </div>
            </div>
          </Space>
        </Card>

        {!isTon && (
          <Card size="small" style={{ borderRadius: '14px', border: '1px solid #e2e8f0' }} bodyStyle={{ padding: '14px' }}>
            <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
              {linkedOnly
                ? t('Already sent from your linked wallet? Paste the transaction hash')
                : t('Already sent from the connected wallet? Paste the transaction hash')}
            </Text>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={manualHash}
                onChange={(e) => setManualHash(e.target.value)}
                placeholder={isEvmNetwork(activeNetwork) ? '0x…' : t('Transaction hash')}
                data-testid="deposit-hash"
              />
              <Button type="primary" loading={isSending} onClick={handleManualReport} disabled={!manualHash.trim()}>
                {t('Check')}
              </Button>
            </Space.Compact>

            {deposits.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>{t('Recent deposits')}</Text>
                  <Button type="text" size="small" icon={<ReloadOutlined />} onClick={() => loadDeposits()} />
                </div>
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  {deposits.map((d) => (
                    <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                      <Space size={6}>
                        <Tag color={STATUS_COLOR[d.status]} style={{ marginRight: 0 }}>{t(d.status)}</Tag>
                        <Text>{d.amount !== null ? `${formatAmount(d.currency || '', d.amount)} ${currencyLabel(d.currency || '')}` : '—'}</Text>
                      </Space>
                      {d.explorer_url ? (
                        <a href={d.explorer_url} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'ui-monospace, monospace' }}>
                          {d.tx_hash.slice(0, 6)}…{d.tx_hash.slice(-4)}
                        </a>
                      ) : (
                        <Text type="secondary">{d.tx_hash.slice(0, 6)}…</Text>
                      )}
                    </div>
                  ))}
                </Space>
              </div>
            )}
          </Card>
        )}
      </Space>
      </div>
  );

  if (inline) {
    if (!open) return null;
    return (
      <div className="kf-panel">
        <div className="kf-panel-head">
          <h2 className="kf-panel-title">
            {voucherMode ? t('Doubling Voucher — Top up') : t('Top up')}
            {kredafi ? '' : ` · ${meta.name}`}
          </h2>
          <button type="button" className="kf-x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        {form}
      </div>
    );
  }

  return (
    <Drawer
      title={
        <Space size={8}>
          <img src={meta.icon} alt={meta.name} width={20} height={20} style={{ borderRadius: '50%', objectFit: 'contain' }} />
          <span>
            {voucherMode ? t('Doubling Voucher — Top up') : t('Top up')}
            {kredafi ? '' : ` · ${meta.name}`}
          </span>
        </Space>
      }
      placement="bottom"
      onClose={onClose}
      open={open}
      height="auto"
      bodyStyle={{
        padding: '20px 16px 36px 16px',
        borderTopLeftRadius: '20px',
        borderTopRightRadius: '20px',
        maxHeight: '85vh',
        overflowY: 'auto',
      }}
      headerStyle={{ borderBottom: '1px solid #f0f0f0' }}
    >
      {form}
    </Drawer>
  );
};

export default MultiNetworkDepositDrawer;
